import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Blocks } from '../../components/sfcc-content-rendering/Blocks';
import { HtmlSnippet } from '../../components/sfcc-content-rendering/HtmlSnippet';
import { ProductsAddedToBagDrawer } from '../../contexts/products-added-to-bag-drawer/ProductsAddedToBagDrawer';
import { useGTM } from '../../hooks';
import { Breadcrumb, Header, Text } from '../../ui-components';
import { useBreadcrumbs } from '../../ui-components/breadcrumbs/useBreadcrumbs';
import { BreadcrumbSchema } from '../../utils/schema';
import { getOriginUrl } from '../../utils/url-utils';
import { SearchHead } from './SearchHead';
import { FilterDrawer } from './components/filters/FilterDrawer';
import { FilterButtons } from './components/filters/filters-buttons/FilterButtons';
import { CategorySearchNoResult } from './components/no-results/CategorySearchNoResult';
import { SearchListing } from './components/search-listing/SearchListing';
import { SearchSkeletons } from './components/skeletons/SearchSkeletons';
import { BloomreachProductType } from './hooks/use-search';
import { useSearch } from './hooks/use-search/useSearch';
import {
    cleanPathname,
    convertSearchParamsToURLParams,
    convertURLParamsToSearchParams,
} from './utils/search-url-params-utils';
import type { Block } from '../../components/sfcc-content-rendering/Blocks';
import type { CategoryInfo } from '../show/api/getContentData';

interface CategorySearchProps {
    categoryInfo?: CategoryInfo;
}

const CategorySearch: FC<CategorySearchProps> = ({ categoryInfo }) => {
    const category = categoryInfo?.response?.category;

    const pagePlacementBlocks = useMemo<Map<number, Block[]>>(() => {
        const c_productInfoTile: string[] | undefined = category?.c_productInfoTile;
        const contentAssets: Record<string, Block[]> =
            categoryInfo?.response?.categoryContent?.contentAssets ?? {};
        const productInfoTile = c_productInfoTile
            ?.map((x) => x.split(','))
            ?.map((r): [number, string] => [parseInt(r[0], 10), String(r[1]).trim()]);
        const entries: [number, Block[]][] =
            productInfoTile?.map(([position, contentAssetId]) => [
                position,
                contentAssets[contentAssetId] ?? [],
            ]) ?? [];
        return new Map<number, Block[]>(entries.filter(([_, blocks]) => blocks.length > 0));
    }, [category?.c_productInfoTile, categoryInfo?.response?.categoryContent?.contentAssets]);

    const location = useLocation();
    const search = useSearch(BloomreachProductType.hardgood, cleanPathname(location.pathname));
    const { updatePageData, onPlpPageView } = useGTM();

    useEffect(() => {
        if (category) {
            onPlpPageView(category);
        }
    }, [category, location]);

    useEffect(() => {
        updatePageData({
            search: {
                products: search.items,
                params: search.searchParams,
            },
        });
    }, [updatePageData, search.searchParams, search.items]);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [expandedFilters, setExpandedFilters] = useState<number[]>(
        // all filters start closed by default
        [],
    );
    const [scrollToFilterIndex, setScrollToFilterIndex] = useState<number | null>(null);

    const handleExpandedFiltersChange = useCallback((indices: number | number[]) => {
        if (Array.isArray(indices)) {
            setExpandedFilters(indices);
        }
    }, []);

    const onCloseFilterDrawer = useCallback(() => {
        onClose();
        setScrollToFilterIndex(null);
    }, [onClose]);

    const onOpenFilterDrawer = useCallback(
        (filterIndex: number) => {
            // -1 means "don't auto-open any filter" (used when clicking All Filters with active selections)
            if (
                filterIndex !== undefined &&
                filterIndex >= 0 &&
                !expandedFilters.includes(filterIndex)
            ) {
                setExpandedFilters((prev) => [...prev, filterIndex]);
            }
            setScrollToFilterIndex(filterIndex >= 0 ? filterIndex : null);
            onOpen();
        },
        [onOpen, expandedFilters],
    );

    const breadcrumbs = useBreadcrumbs(categoryInfo?.response?.category?.parentCategoryTree);

    const canonicalUrl = useMemo(() => {
        const urlParams = new URLSearchParams(location.search);
        const searchParamsFromUrl = convertURLParamsToSearchParams({
            urlParams,
            productType:
                (urlParams.get('productType') as BloomreachProductType) ??
                BloomreachProductType.hardgood,
        });
        const params = convertSearchParamsToURLParams({
            productType: searchParamsFromUrl.productType,
            page: searchParamsFromUrl.page,
        }).toString();
        return `${getOriginUrl()}${location.pathname}${params ? `?${params}` : ''}`;
    }, [location.search, location.pathname]);

    const prevUrl = useMemo(() => {
        if (!canonicalUrl) {
            return undefined;
        }
        const urlParams = new URL(canonicalUrl).searchParams;
        const pageStr = urlParams.get('page');
        urlParams.delete('page');
        const page = pageStr ? parseInt(pageStr, 10) : 1;
        if (page <= 1) {
            // we are on the first page
            return undefined;
        }
        if (page === 2) {
            // we are on the second page, so we don't need to add the page parameter
            return `${getOriginUrl()}${location.pathname}`;
        }
        urlParams.set('page', (page - 1).toString());

        return `${getOriginUrl()}${location.pathname}?${urlParams.toString()}`;
    }, [canonicalUrl, location.pathname]);

    const nextUrl = useMemo(() => {
        if (!canonicalUrl) {
            return undefined;
        }
        if (!search.hasNextPage) {
            return undefined;
        }
        const urlParams = new URL(canonicalUrl).searchParams;
        const pageStr = urlParams.get('page');
        const page = pageStr ? parseInt(pageStr, 10) : 1;
        urlParams.set('page', (page + 1).toString());
        return `${getOriginUrl()}${location.pathname}?${urlParams.toString()}`;
    }, [canonicalUrl, location.pathname, search.hasNextPage]);

    if (search.isLoading) {
        // TODO: Add a skeleton for the category search results, for now we're using the search skeletons
        return <SearchSkeletons />;
    }

    return (
        <div className="mx-auto h-full w-full max-w-[1928px]">
            <SearchHead canonicalUrl={canonicalUrl} prevUrl={prevUrl} nextUrl={nextUrl} />
            <div className="flex flex-col gap-4 px-0 py-4 sm:px-8 lg:px-10 xl:px-16">
                <div className="hidden justify-start px-4 sm:px-0 md:flex">
                    <Breadcrumb itemsList={breadcrumbs} />
                </div>
                <BreadcrumbSchema breadcrumbTrail={breadcrumbs} />
                <Flex direction="column" gap={6}>
                    <Flex direction="column" gap={6} paddingX={{ base: 4, sm: 0 }}>
                        <Blocks
                            contentBlocks={categoryInfo?.response?.categoryContent?.categorySlots}
                            id="product-listing-slot-3"
                        />
                        <Blocks
                            contentBlocks={categoryInfo?.response?.categoryContent?.categorySlots}
                            id="product-listing-slot-1"
                        />
                        <Blocks
                            contentBlocks={categoryInfo?.response?.categoryContent?.categorySlots}
                            id="product-listing-slot-4"
                        />
                    </Flex>

                    <Flex flexDirection="column" gap={4}>
                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                            paddingX={{ base: 4, sm: 0 }}
                        >
                            <Header
                                id="search.results.for.category"
                                color="slt-pepper.1000"
                                as="h1"
                                variant="header4"
                                style="bold"
                            >
                                {categoryInfo?.response?.category?.name}
                            </Header>
                            <Text data-testid="category-product-count">
                                <FormattedMessage
                                    id="product.count"
                                    defaultMessage="{count, plural, =0 {0 Items} one {# Item} other {# Items}}"
                                    values={{ count: Math.max(0, search.total) }}
                                />
                            </Text>
                        </Flex>

                        <FilterButtons search={search} onOpenFilterDrawer={onOpenFilterDrawer} />

                        {search.total === 0 ? (
                            <CategorySearchNoResult />
                        ) : (
                            <SearchListing
                                id="category-search"
                                search={search}
                                pagePlacementBlocks={pagePlacementBlocks}
                                source="PLP"
                            />
                        )}
                    </Flex>

                    <Flex direction="column" gap={6} paddingX={{ base: 4, sm: 0 }}>
                        <Blocks
                            id="product-listing-slot-5"
                            contentBlocks={categoryInfo?.response?.categoryContent?.globalSlots}
                        />
                        <Blocks
                            id="product-listing-slot-6"
                            contentBlocks={categoryInfo?.response?.categoryContent?.categorySlots}
                        />
                        <Blocks
                            id="cat-landing-bottom-block"
                            contentBlocks={categoryInfo?.response?.categoryContent?.categorySlots}
                        />
                        <Blocks
                            id="seo-content-block-js-css"
                            contentBlocks={categoryInfo?.response?.categoryContent?.contentAssets}
                        />
                        <HtmlSnippet
                            id="seo-content"
                            html={categoryInfo?.response?.category?.c_seo ?? ''}
                        />
                    </Flex>
                </Flex>
            </div>
            <FilterDrawer
                search={search}
                isOpen={isOpen}
                onClose={onCloseFilterDrawer}
                expandedFilters={expandedFilters}
                onExpandedFiltersChange={handleExpandedFiltersChange}
                scrollToFilterIndex={scrollToFilterIndex}
            />
            <ProductsAddedToBagDrawer />
        </div>
    );
};

CategorySearch.defaultProps = {
    categoryInfo: undefined,
};

export default CategorySearch;
